import {Spinner} from '@components/ui/spinner'
import {ErrorBox} from '@components/commons/error-box'
import {useTranslation} from '@/translations/i18n.tsx'
import {AlertTriangleIcon, CheckIcon, SearchLgIcon} from '@components/ui/icon'
import {FC, ReactElement, ReactNode, useMemo, useState} from 'react'
import {useCountries} from '@features/settings/queries/useCountries.ts'
import {
    Combobox,
    ComboboxContent,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
    ComboboxTrigger
} from '@components/ui/combobox-atoms'
import {Separator} from '@components/ui/separator'
import {SelectedCountry} from '@features/settings/components/add-areas-modal'
import {InputHelpText} from '@components/ui/input-help-text'
import {cn, raise} from '@utilities/helpers'

interface CountriesSelectProps {
    selectedCountry: SelectedCountry
    onChangeCountry: (value: SelectedCountry) => void
    region: string | null
    trigger?: ReactElement
    isMobile: boolean
    onCloseCountryMobileContent?: () => void
}

export const CountriesSelect: FC<CountriesSelectProps> = ({
    onChangeCountry,
    region,
    selectedCountry,
    trigger,
    isMobile,
    onCloseCountryMobileContent
}) => {
    const {t} = useTranslation()
    const selectedRegion = region ?? raise('selectedRegion is nullish')
    const countriesQuery = useCountries({region: selectedRegion})
    const [search, setSearch] = useState('')

    const filteredCountries = useMemo(() => {
        if (countriesQuery.data) {
            return search
                ? countriesQuery.data.countries.filter(country =>
                      country.extended_country.toLowerCase().includes(search.toLowerCase())
                  )
                : countriesQuery.data.countries
        }
        return []
    }, [countriesQuery.data, search])

    const handleSelect = (countryCode: string | null) => {
        if (!countryCode) return
        const country = countriesQuery.data?.countries.find(item => item.country === countryCode)
        if (!country) return
        onChangeCountry({label: country.extended_country, country_code: country.country})
        onCloseCountryMobileContent?.()
    }

    const content: ReactNode = (
        <>
            <ComboboxInput
                startSlot={<SearchLgIcon size={20} className="text-neutral-500" />}
                variant={isMobile ? 'primary' : 'ghost'}
                inputSize="sm"
                placeholder={t('settings:areas:search_country_placeholder')}
            />

            {!isMobile && <Separator orientation="horizontal" />}
            {countriesQuery.isPending ? (
                <div className="flex w-full justify-center p-4">
                    <Spinner size={24} />
                </div>
            ) : countriesQuery.isError ? (
                <ErrorBox icon={<AlertTriangleIcon />} title={t('errors:default')} />
            ) : (
                <ComboboxList className="flex flex-col gap-px py-1 px-1.5 overflow-y-auto scrollbar-none [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                    {filteredCountries.length ? (
                        filteredCountries.map(({country, extended_country}) => {
                            const isSelected = country == selectedCountry.country_code
                            return (
                                <ComboboxItem
                                    key={country}
                                    value={country}
                                    className={cn(
                                        'justify-between rounded-md py-2.5 px-4 text-md font-normal',
                                        isSelected && 'bg-neutral-100 [&_svg]:fill-primary-600'
                                    )}
                                >
                                    {extended_country}
                                    {isSelected && <CheckIcon size={20} />}
                                </ComboboxItem>
                            )
                        })
                    ) : (
                        <div className="flex justify-center">
                            <InputHelpText helpText={t('commons:no_results')} />
                        </div>
                    )}
                </ComboboxList>
            )}
        </>
    )

    return (
        <Combobox
            items={filteredCountries.map(country => country.country)}
            filter={null}
            onInputValueChange={setSearch}
            onValueChange={handleSelect}
            {...(isMobile ? {open: true} : {})}
        >
            {isMobile ? (
                <div className="grid grid-rows-[auto_minmax(0,1fr)] h-full w-full px-4">{content}</div>
            ) : (
                <>
                    {trigger && <ComboboxTrigger render={trigger} nativeButton={false} />}
                    <ComboboxContent
                        positionerClassName="z-tooltip"
                        className="grid max-h-[270px] w-[390px] grid-rows-[auto_1px_minmax(0,1fr)] p-0"
                    >
                        {content}
                    </ComboboxContent>
                </>
            )}
        </Combobox>
    )
}
